import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, ArrowRight, Trash, CheckCircle, XCircle, Lightning, Clock } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY_META = {
  low:    { label: 'Low',    bg: 'bg-zinc-50',  text: 'text-zinc-600',  border: 'border-zinc-200' },
  medium: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  high:   { label: 'High',   bg: 'bg-red-50',   text: 'text-red-700',   border: 'border-red-200' },
};

const STATUS_META = {
  new:        { label: 'New',        bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  in_review:  { label: 'In Review',  bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  actioned:   { label: 'Actioned',   bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  rejected:   { label: 'Rejected',   bg: 'bg-zinc-100',   text: 'text-zinc-500',    border: 'border-zinc-300' },
};

export const ItemSuggestions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [suggestions, setSuggestions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ description: '', unit: 'Sqft', trade_id: '', urgency: 'medium', remarks: '', proposed_rate: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const [sRes, tRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/item-suggestions${q}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
      ]);
      setSuggestions(sRes.data);
      setTrades(tRes.data);
    } catch { toast.error('Failed to load suggestions'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/item-suggestions`, {
        description: form.description.trim(),
        unit: form.unit || 'Sqft',
        trade_id: form.trade_id || '',
        urgency: form.urgency || 'medium',
        remarks: form.remarks || '',
        proposed_rate: form.proposed_rate ? parseFloat(form.proposed_rate) : null,
      }, { withCredentials: true });
      toast.success('Suggestion submitted — Admin will review');
      setDialogOpen(false);
      setForm({ description: '', unit: 'Sqft', trade_id: '', urgency: 'medium', remarks: '', proposed_rate: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const promote = async (s) => {
    if (!window.confirm(`Promote "${s.description}" to Master Studio as a draft? You'll complete recipes there.`)) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/item-suggestions/${s.id}/promote-to-studio`, {}, { withCredentials: true });
      toast.success(`Created Studio draft ${res.data.item_code}`);
      navigate('/master-studio');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const reject = async (s) => {
    const reason = window.prompt('Reason for rejection (shown to requester):');
    if (reason === null) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/item-suggestions/${s.id}`, {
        status: 'rejected', admin_remarks: reason,
      }, { withCredentials: true });
      toast.success('Suggestion rejected');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (s) => {
    if (!window.confirm('Delete this suggestion permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/item-suggestions/${s.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const counts = suggestions.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800" data-testid="suggestions-title">Item Suggestions</h2>
            <p className="text-sm text-zinc-600 mt-1">Team-submitted requests to add new items to the Line Item Master via Master Studio.</p>
            <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-2 mt-3 max-w-3xl">
              <strong>Flow:</strong> Team member files a suggestion → Admin promotes to Master Studio (draft) → Admin adds material & manpower recipes → Approve → Publish → Item lands in Line Item Master and becomes available in BOQ typeahead.
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="new-suggestion-btn">
            <Plus size={16} className="mr-1.5" /> New Suggestion
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-xs text-zinc-500">Filter:</span>
          {['', 'new', 'in_review', 'actioned', 'rejected'].map(s => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1 border rounded-none ${statusFilter === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50'}`}
              data-testid={`filter-${s || 'all'}`}
            >
              {s === '' ? `All (${suggestions.length})` : `${STATUS_META[s]?.label || s} (${counts[s] || 0})`}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : suggestions.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
            No suggestions yet. Use <strong>New Suggestion</strong> to request a new item.
          </div>
        ) : (
          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-zinc-500 uppercase font-bold tracking-wider">
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Trade</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2">Urgency</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">By</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const urg = URGENCY_META[s.urgency] || URGENCY_META.medium;
                  const st = STATUS_META[s.status] || STATUS_META.new;
                  return (
                    <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`suggestion-row-${s.id}`}>
                      <td className="px-3 py-2 font-medium text-zinc-800">
                        {s.description}
                        {s.remarks && <div className="text-[10px] text-zinc-500 mt-1">{s.remarks}</div>}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">{s.trade_name || '-'}</td>
                      <td className="px-3 py-2 text-zinc-600">{s.unit}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 border ${urg.bg} ${urg.text} ${urg.border}`}>{urg.label}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                      </td>
                      <td className="px-3 py-2 text-zinc-600">
                        {s.suggested_by_name}
                        <div className="text-[10px] text-zinc-400">{s.suggested_at ? new Date(s.suggested_at).toLocaleDateString() : ''}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-500">{s.source_project_code || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && (s.status === 'new' || s.status === 'in_review') && (
                          <div className="flex gap-1 justify-end">
                            <Button
                              onClick={() => promote(s)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-none"
                              data-testid={`promote-${s.id}`}
                              disabled={!!s.linked_studio_item_id}
                              title={s.linked_studio_item_id ? 'Already promoted to Studio' : 'Promote to Master Studio as draft'}
                            >
                              <ArrowRight size={12} className="mr-1" /> To Studio
                            </Button>
                            <Button
                              onClick={() => reject(s)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-[10px] border-red-300 text-red-700 hover:bg-red-50 rounded-none"
                              data-testid={`reject-${s.id}`}
                            >
                              <XCircle size={12} className="mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {isAdmin && s.status === 'actioned' && (
                          <span className="text-[10px] text-emerald-600 flex items-center gap-1 justify-end"><CheckCircle size={12} /> Published</span>
                        )}
                        {isAdmin && s.status === 'rejected' && (
                          <Button onClick={() => remove(s)} variant="outline" size="sm" className="h-7 px-2 text-[10px] border-zinc-300 text-zinc-500 hover:bg-zinc-50 rounded-none" data-testid={`delete-${s.id}`}>
                            <Trash size={12} />
                          </Button>
                        )}
                        {!isAdmin && <span className="text-[10px] text-zinc-400">-</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* New Suggestion Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-lg" data-testid="new-suggestion-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Suggest a New Line Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Description *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none text-sm" required data-testid="sg-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Unit *</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 rounded-none text-sm" required data-testid="sg-unit" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Trade</Label>
                <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" data-testid="sg-trade">
                  <option value="">-- Select --</option>
                  {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Urgency</Label>
                <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" data-testid="sg-urgency">
                  <option value="low">Low — can wait</option>
                  <option value="medium">Medium — needed this week</option>
                  <option value="high">High — blocking a quote</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Proposed Rate (₹)</Label>
                <Input type="number" value={form.proposed_rate} onChange={(e) => setForm({ ...form, proposed_rate: e.target.value })} className="mt-1 rounded-none text-sm" data-testid="sg-rate" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Remarks / Specification</Label>
              <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={3} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" placeholder="Spec, expected make, context" data-testid="sg-remarks" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setDialogOpen(false)} variant="outline" className="rounded-none border-zinc-300">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="sg-submit">
                {submitting ? 'Submitting…' : 'Submit Suggestion'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemSuggestions;
